import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { GrayArrowUp } from '@/theme/overrides/CustomIcons';
import {
  CircleBox,
  Layout,
  MainContent,
  MainContentInner,
  SidebarContainer,
  SidebarContent,
  SidebarInner,
  StyledSidebar,
  ToggleButton,
} from '@/components/HoverLeftDrawerLayout/styles';
import { BoxProps } from '@mui/material/Box';
import { throttle } from 'lodash-es';
import { useAtom } from 'jotai';
import { orgLayoutState } from '@/recoil/atom';
import classNames from 'classnames';

type P = {
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  mainRef?: React.RefObject<HTMLDivElement | null>;
  gnbHeight?: number;
  forceInitExpanded?: boolean;
  newHomeMode?: boolean;
  // 작은화면(1440 px 미만)에서 LNB 가 위에 겹칠지, 밀릴 지 판단
  isSliding: boolean;
  // 우측 Content 화면 최소 너비
  contentMinWidth?: number;
  /**
   * 우측 Content 가 뷰포트(100vh) 대신 **부모가 준 높이**를 쓰고 스크롤을 만들지 않는다.
   * 안쪽에서 스스로 스크롤을 관리하는 화면(캘린더 격자 등)에 쓴다 — 기본값은 기존 동작.
   */
  fillHeight?: boolean;
  /** 사이드바 배경. 기본은 조직도·소셜·전자결재가 쓰는 Gray1 */
  sidebarBackground?: string;
} & BoxProps;

const THROTTLE_VALUE = 200;

const VIEWPORT_WIDTH_1 = 1440; // 1024 기준으로 커지거나 작아질 때 리사이즈시 자동으로 열리고 닫히기 위함.
const VIEWPORT_WIDTH_2 = 2160;
const VIEWPORT_WIDTH_3 = 2561;

/**
 * 뷰포트 폭. **렌더 중에도 호출되므로 `window` 를 직접 읽으면 안 된다.**
 *
 * 이 컴포넌트는 `useState` 초기값과 토글 버튼의 `style` 을 뷰포트 폭으로 정하는데,
 * 둘 다 렌더 중에 평가된다. 서버에는 `window` 가 없어 SSR 이 그대로 죽는다
 * (`/test/calendar/month` 가 `ReferenceError: window is not defined` 로 500 이었다).
 * 조직도·소셜·전자결재는 서버에서 이 컴포넌트까지 도달하지 않아 그동안 안 드러났을 뿐이다.
 *
 * 서버에서는 데스크톱(넓은 화면)으로 가정한다 — 기존 데스크톱 동작과 같은 분기로 떨어진다.
 */
const getViewportWidth = () =>
  typeof window === 'undefined' ? VIEWPORT_WIDTH_1 : window.innerWidth;

export default function HoverLeftDrawerLayout({
  leftContent,
  rightContent,
  mainRef,
  gnbHeight,
  forceInitExpanded,
  newHomeMode = false,
  isSliding,
  contentMinWidth,
  fillHeight,
  sidebarBackground,
  className,
  ...props
}: P) {
  const [state, setState] = useAtom(orgLayoutState);
  const [isHovering, setIsHovering] = useState(false); // 호버 상태
  const [wasAboveThreshold, setWasAboveThreshold] = useState(
    getViewportWidth() >= VIEWPORT_WIDTH_1,
  );

  const resizeListener = useCallback(
    throttle(() => {
      const isNowAboveThreshold = getViewportWidth() >= VIEWPORT_WIDTH_1;
      let resultIsExpanded = state.isExpanded;

      if (!wasAboveThreshold && isNowAboveThreshold && !state.isExpanded) {
        handleDrawerOpen();
        resultIsExpanded = true;
      } else if (
        wasAboveThreshold &&
        !isNowAboveThreshold &&
        state.isExpanded
      ) {
        handleDrawerClose();
        resultIsExpanded = false;
      }

      ViewPortColumnsSet(resultIsExpanded);
      setWasAboveThreshold(isNowAboveThreshold);
    }, THROTTLE_VALUE),
    [wasAboveThreshold, state.isExpanded],
  );

  useEffect(() => {
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeListener]);

  useEffect(() => {
    if (!state.isRender) {
      if (forceInitExpanded !== undefined) {
        toggleDrawer(forceInitExpanded);
      } else {
        const isNowAboveThreshold = getViewportWidth() >= VIEWPORT_WIDTH_1;
        toggleDrawer(isNowAboveThreshold);
      }
    }
  }, [forceInitExpanded]);

  const toggleDrawer = useCallback((expanded: boolean) => {
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      setIsHovering(expanded);
      ViewPortColumnsSet(false);
    } else {
      setIsHovering(false);
      ViewPortColumnsSet(expanded);
    }
  }, []);

  const handleDrawerOpen = () => {
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      setIsHovering(true);
      setState({
        ...state,
        isExpanded: false,
      });
    } else {
      toggleDrawer(true);
    }
  };
  const handleDrawerClose = () => {
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      setIsHovering(false);
      setState({
        ...state,
        isExpanded: false,
      });
    } else {
      toggleDrawer(false);
    }
  };

  const ViewPortColumnsSet = (resultIsExpanded: boolean) => {
    let columns;
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      columns = 3;
    } else if (getViewportWidth() < VIEWPORT_WIDTH_2) {
      columns = resultIsExpanded ? 3 : 4;
    } else if (getViewportWidth() < VIEWPORT_WIDTH_3) {
      columns = resultIsExpanded ? 4 : 5;
    } else {
      columns = resultIsExpanded ? 5 : 6;
    }

    setState({
      ...state,
      columns: columns,
      isExpanded: resultIsExpanded,
      stickyFlexDirection:
        getViewportWidth() - (resultIsExpanded ? 360 : 0) < 900
          ? 'column'
          : 'row',
      isRender: true,
    });
  };

  const handleMouseEnterSidebar = (e: React.MouseEvent) => {
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      return;
    }
    if (!(e.target instanceof HTMLButtonElement) && !state.isExpanded) {
      setIsHovering(true);
    }
  };

  const handleMouseLeaveSidebar = () => {
    if (getViewportWidth() < VIEWPORT_WIDTH_1) {
      return;
    }
    if (!state.isExpanded) setIsHovering(false);
  };

  return (
    <Layout className={classNames([className, { newHomeMode }])} {...props}>
      <SidebarContainer
        isExpanded={state.isExpanded}
        isSliding={isSliding}
        isHovering={isHovering}
      >
        <StyledSidebar
          onMouseEnter={handleMouseEnterSidebar}
          onMouseLeave={handleMouseLeaveSidebar}
          isExpanded={state.isExpanded}
          isHovering={isHovering && !state.isExpanded}
          gnbHeight={gnbHeight}
          newHomeMode={newHomeMode}
          sidebarBackground={sidebarBackground}
        >
          <ToggleButton
            className={'toggle-button'}
            aria-label="open/close drawer"
            onClick={() =>
              toggleDrawer(
                getViewportWidth() < VIEWPORT_WIDTH_1
                  ? !isHovering
                  : !state.isExpanded,
              )
            }
            style={{
              pointerEvents: 'auto',
              transform:
                getViewportWidth() < VIEWPORT_WIDTH_1
                  ? isHovering
                    ? 'rotate(-90deg)'
                    : 'rotate(90deg)'
                  : state.isExpanded
                  ? 'rotate(-90deg)'
                  : 'rotate(90deg)',
            }}
          >
            <CircleBox>
              <GrayArrowUp width={24} height={24} />
            </CircleBox>
          </ToggleButton>

          <SidebarContent visible={state.isExpanded || isHovering}>
            <SidebarInner>{leftContent}</SidebarInner>
          </SidebarContent>
        </StyledSidebar>
      </SidebarContainer>

      <MainContent
        isExpanded={state.isExpanded}
        isSliding={isSliding}
        isHovering={isHovering}
        contentMinWidth={contentMinWidth}
      >
        <MainContentInner
          ref={mainRef}
          gnbHeight={gnbHeight}
          fillHeight={fillHeight}
        >
          {rightContent}
        </MainContentInner>
      </MainContent>
    </Layout>
  );
}
